import { useState } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import Button from "@/v2-backoffice-client/components/button"
import { useLicenseAccountMatchesQuery } from "@/v2-backoffice-client/license/account/view/license.account.matches.query"
import LicenseAccountMatchTable from "@/v2-backoffice-client/license/account/view/LicenseAccountMatchTable"
import LicenseAccountReviewDialog from "@/v2-backoffice-client/license/account/view/LicenseAccountReviewDialog"
import type { LicenseAccountFragment } from "@/v2-backoffice-client/license/account/__types__/license.account.fragment"

export type LicenseAccountMatchPanelProps = {
  account: LicenseAccountFragment
}

// The panel's Customers tab: who this account's org number points at, and
// the two decisions an operator can record about it. LINK when one of the
// candidates is the paying customer (usage signal shows which duplicate
// carries the users); ACKNOWLEDGE when the sharing is intentional — group
// billing via one entity (children get operator licenses), a duplicate
// account finance is consolidating — so the row leaves the needs-review
// worklist with the why written down. Owns the matches query; the table is
// presentational, mirroring the Invoices tab.
function LicenseAccountMatchPanel(props: LicenseAccountMatchPanelProps) {
  const { account } = props
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false)

  const { data, loading, error, refetch } = useLicenseAccountMatchesQuery({
    variables: { accountId: account.Id },
  })
  const matches = data?.getBillingAccountMatches ?? []

  const isLinked =
    account.CustomerId !== null && account.CustomerId !== undefined
  const isAcknowledged = Boolean(account.ReviewedAt)
  const canReview = !isLinked && !account.IsArchived

  return (
    <div className="flex h-full min-h-0 flex-col">
      {canReview && (
        <div className="mx-2 mt-2 flex items-start justify-between gap-4 rounded-md border bg-muted/30 px-3 py-2.5">
          <div className="min-w-0 text-sm">
            {isAcknowledged ? (
              <>
                <p className="font-medium">
                  Acknowledged
                  {account.ReviewedByUserName
                    ? ` by ${account.ReviewedByUserName}`
                    : ""}
                  {account.ReviewedAt
                    ? ` — ${formatDateString(account.ReviewedAt)}`
                    : ""}
                </p>
                <p className="mt-0.5 text-muted-foreground">
                  {account.ReviewNote ?? "No note was left."}
                </p>
              </>
            ) : (
              <>
                <p className="font-medium">
                  {account.UnmatchedReason ?? "Unmatched"}
                </p>
                <p className="mt-0.5 text-muted-foreground">
                  Link the paying customer below — or, if this state is
                  intentional (group billing, duplicate account being
                  consolidated), acknowledge the row so it leaves the
                  needs-review worklist with the reason written down.
                </p>
              </>
            )}
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="shrink-0"
            data-feature-code="UI_BackofficeBillingAccount_Review_Open"
            onClick={() => {
              setIsReviewDialogOpen(true)
            }}
          >
            {isAcknowledged ? "Update Acknowledgment" : "Acknowledge..."}
          </Button>
        </div>
      )}

      <LicenseAccountMatchTable
        className="min-h-0 flex-1"
        account={account}
        matches={matches}
        isLoading={loading}
        error={error}
        onRefresh={() => {
          void refetch()
        }}
      />

      <LicenseAccountReviewDialog
        account={account}
        open={isReviewDialogOpen}
        onOpenChange={setIsReviewDialogOpen}
      />
    </div>
  )
}

export default LicenseAccountMatchPanel
